import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Tabs,
  Tab,
  Switch,
  Select,
  MenuItem,
} from "@mui/material";
import { can } from "../shared utils/auth";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function AdultDetailsTab({
  loading,
  adultDetails = [],
  editable,
  showToast,
}) {
  // The residential charge is the back office's: nulled out and ignored on
  // save by the server for anyone without FINANCE, so the column goes too.
  const showFinancials = can("finance");
  const {
    editRows,
    dirtyIds,
    startEdit,
    cancelEdit,
    changeField,
    saveRow,
    saveAll,
    nsrData,
    changeNsrField,
    fetchNsrForAdult,
    bookingData,
    editedCharges,
    changeResCharge,
    fetchBookingsForAdult,
  } = editable || {};

  const [tab, setTab] = useState(0);

  // Run the bulk save and surface a summary toast. saveAll never throws and
  // reports partial failures, so the user can see what saved and retry the rest.
  const handleSaveAll = async () => {
    const res = await saveAll?.();
    if (!res) return;
    if (res.failed?.length) {
      showToast?.(
        `Saved ${res.saved.length}, ${res.failed.length} failed — failed rows kept for retry.`,
        "warning"
      );
    } else if (res.saved?.length) {
      showToast?.(
        `Saved ${res.saved.length} change${res.saved.length === 1 ? "" : "s"}.`,
        "success"
      );
    }
  };

  const pick = (o, ...keys) => {
    for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
    return "";
  };

  const vCell = (isEditing, raw, id, key, alts = []) =>
    isEditing ? (editRows?.[id]?.[key] ?? "") : pick(raw, key, ...alts);

  // Load NSR and booking data for display rows
  const ensureNsrLoaded = (adultId) => {
    if (!nsrData?.[adultId] && fetchNsrForAdult) {
      fetchNsrForAdult(adultId);
    }
  };

  const ensureBookingsLoaded = (adultId) => {
    if (!bookingData?.[adultId] && fetchBookingsForAdult) {
      fetchBookingsForAdult(adultId);
    }
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} /> <span>Loading adult details…</span>
        </Box>
      ) : adultDetails.length > 0 ? (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Personal" />
            <Tab label="Other Info" />
          </Tabs>

          {dirtyIds?.size > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button variant="contained" onClick={handleSaveAll}>
                Save all changes ({dirtyIds.size})
              </Button>
            </Box>
          )}

          {/* PERSONAL TAB */}
          <TabPanel value={tab} index={0}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Surname</TableCell>
                  <TableCell>Nationality</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="center">Residential?</TableCell>
                  <TableCell>Gender</TableCell>
                  {showFinancials && <TableCell align="right">Charge</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {adultDetails.map((raw) => {
                  const id = pick(raw, "id");
                  const draft = editRows?.[id];
                  const isEditing = !!draft;
                  const nsr = nsrData?.[id];

                  // Ensure NSR and bookings are loaded for display
                  if (!isEditing && !nsr) ensureNsrLoaded(id);
                  if (!bookingData?.[id]) ensureBookingsLoaded(id);

                  const onSite = nsr?.overnightResident ?? false;
                  const nsrGender = nsr?.gender || null;
                  const bd = bookingData?.[id];
                  const currentCharge = bd?.resCharge ?? 0;
                  const displayCharge = editedCharges?.[id] != null ? editedCharges[id] : currentCharge;
                  const hasResBookings = (bd?.bookings?.length || 0) > 0;

                  return (
                    <TableRow key={`ad-personal-${id}`}>
                      <TableCell>{id}</TableCell>

                      {/* Name */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "adultName")}
                            onChange={(e) => changeField(id, "adultName", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "adultName")
                        )}
                      </TableCell>

                      {/* Surname */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "adultSurname")}
                            onChange={(e) => changeField(id, "adultSurname", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "adultSurname")
                        )}
                      </TableCell>

                      {/* Nationality */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "adultNationality")}
                            onChange={(e) => changeField(id, "adultNationality", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "adultNationality") || <em>—</em>
                        )}
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "adultEmail")}
                            onChange={(e) => changeField(id, "adultEmail", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "adultEmail") || <em>—</em>
                        )}
                      </TableCell>

                      {/* Phone */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "adultPhone")}
                            onChange={(e) => changeField(id, "adultPhone", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "adultPhone") || <em>—</em>
                        )}
                      </TableCell>

                      {/* On site? */}
                      <TableCell align="center">
                        {nsr?.loading ? (
                          <CircularProgress size={16} />
                        ) : isEditing ? (
                          <Switch
                            checked={onSite}
                            onChange={(e) => {
                              changeNsrField(id, "overnightResident", e.target.checked);
                              if (!e.target.checked) {
                                changeNsrField(id, "gender", null);
                              }
                            }}
                            size="small"
                          />
                        ) : (
                          onSite ? "Yes" : "No"
                        )}
                      </TableCell>

                      {/* Gender (only relevant if on site) */}
                      <TableCell>
                        {nsr?.loading ? (
                          <CircularProgress size={16} />
                        ) : isEditing && onSite ? (
                          <Select
                            value={nsrGender || ""}
                            onChange={(e) => changeNsrField(id, "gender", e.target.value || null)}
                            variant="standard"
                            size="small"
                            displayEmpty
                            sx={{ minWidth: 90 }}
                          >
                            <MenuItem value=""><em>—</em></MenuItem>
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                          </Select>
                        ) : onSite ? (
                          nsrGender || <em>—</em>
                        ) : (
                          <em style={{ color: "#bbb" }}>n/a</em>
                        )}
                      </TableCell>

                      {/* Residential charge */}
                      {showFinancials && (
                      <TableCell align="right">
                        {bd?.loading ? (
                          <CircularProgress size={16} />
                        ) : !onSite || !hasResBookings ? (
                          <em style={{ color: "#bbb" }}>—</em>
                        ) : isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={displayCharge}
                            onChange={(e) => changeResCharge(id, e.target.value)}
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ width: 100 }}
                          />
                        ) : (
                          `£${Number(currentCharge).toFixed(2)}`
                        )}
                      </TableCell>
                      )}

                      {/* Actions */}
                      <TableCell align="right">
                        {isEditing ? (
                          <>
                            <Button size="small" onClick={() => saveRow(id)} sx={{ mr: 1 }}>
                              Save
                            </Button>
                            <Button size="small" onClick={() => cancelEdit(id)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button size="small" onClick={() => startEdit(raw)}>
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>

          {/* OTHER INFO TAB */}
          <TabPanel value={tab} index={1}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Adult</TableCell>
                  <TableCell>Allergies</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {adultDetails.map((raw) => {
                  const id = pick(raw, "id");
                  const draft = editRows?.[id];
                  const isEditing = !!draft;

                  const nameSource = draft || raw;
                  const nm = pick(nameSource, "adultName");
                  const sn = pick(nameSource, "adultSurname");
                  const fullName = `${nm || ""} ${sn || ""}`.trim();

                  return (
                    <TableRow key={`ad-other-${id}`}>
                      <TableCell>{fullName || id}</TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "allergies")}
                            onChange={(e) => changeField(id, "allergies", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "allergies")
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "notes")}
                            onChange={(e) => changeField(id, "notes", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          vCell(false, raw, id, "notes")
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {isEditing ? (
                          <>
                            <Button size="small" onClick={() => saveRow(id)} sx={{ mr: 1 }}>
                              Save
                            </Button>
                            <Button size="small" onClick={() => cancelEdit(id)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button size="small" onClick={() => startEdit(raw)}>
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>
        </>
      ) : (
        <Typography variant="body2">
          <em>No adult details available.</em>
        </Typography>
      )}
    </Box>
  );
}
